import { Compatibility, Orientation, SceneObject, Vector2 } from '@cgs/wge';

export interface CameraState {
  position: Vector2;
  scale: Vector2;
  rotation: number;
}

const getDefaultCameraState = (): CameraState => ({
  position: new Vector2(0, 0),
  scale: new Vector2(1, 1),
  rotation: 0,
});

const DEFAULT_ORIENTATION = Orientation.Landscape;

export const CAMERA_CONTROL_COMPONENT = Symbol('CAMERA_CONTROL_COMPONENT');

export class CameraControlComponent {
  public _state: Record<Orientation, CameraState> = {
    [Orientation.Portrait]: getDefaultCameraState(),
    [Orientation.Landscape]: getDefaultCameraState(),
  };

  private readonly _sceneObjects: Map<SceneObject, CameraState> = new Map();

  public getState(orientation?: Orientation): CameraState {
    return this._state[orientation || DEFAULT_ORIENTATION];
  }

  public registerScene(...sceneObjects: SceneObject[]): void {
    sceneObjects.forEach((sceneObject) => {
      const defaultState = {
        position: sceneObject.position.clone(),
        scale: sceneObject.scale.clone(),
        rotation: sceneObject.rotation,
      };
      this._sceneObjects.set(sceneObject, defaultState);

      this.updateSceneParams(sceneObject, defaultState);
    });
  }

  public updateSceneInitialState(sceneObject: SceneObject): void {
    const defaultState = this._sceneObjects.get(sceneObject);
    if (defaultState) {
      defaultState.position = sceneObject.position.clone();
      defaultState.scale = sceneObject.scale.clone();
      defaultState.rotation = sceneObject.rotation;
    }
  }

  public unregisterScene(...sceneObjects: SceneObject[]): void {
    sceneObjects.forEach((sceneObject) => {
      const state = this._sceneObjects.get(sceneObject);

      if (state) {
        sceneObject.position = state.position;
        sceneObject.scale = state.scale;
        sceneObject.rotation = state.rotation;

        this._sceneObjects.delete(sceneObject);
      }
    });
  }

  public setPosition(position: Vector2, orientation?: Orientation): void {
    this.setState({ position }, orientation);
  }

  public setScale(scale: Vector2, orientation?: Orientation): void {
    this.setState({ scale }, orientation);
  }

  public setRotation(rotation: number, orientation?: Orientation) {
    this.setState({ rotation }, orientation);
  }

  public setState(state: Partial<CameraState>, orientation?: Orientation): void {
    const orientations = orientation
      ? [orientation]
      : [Orientation.Landscape, Orientation.Portrait];

    orientations.forEach((orientation) => {
      this._state[orientation] = {
        position: state.position || this._state[orientation].position,
        scale: state.scale || this._state[orientation].scale,
        rotation: state.rotation ?? this._state[orientation].rotation,
      };
    });

    this.resize();
  }

  public resize(): void {
    this._sceneObjects.forEach((initialState, sceneObject) =>
      this.updateSceneParams(sceneObject, initialState)
    );
  }

  private get currentOrientation(): Orientation {
    return Compatibility.orientation;
  }

  private updateSceneParams(sceneObject: SceneObject, initialState: CameraState): void {
    const cameraState = this.getState(this.currentOrientation);
    sceneObject.position = initialState.position.add(cameraState.position);
    sceneObject.scale = initialState.scale.multiply(cameraState.scale);
    sceneObject.rotation = initialState.rotation + cameraState.rotation;
  }
}
